import type { Spec } from 'vega';

import type { ChatChartRecipe } from './chatChartRecipe';

export type ChatChartColors = {
  font: string;
  grid: string;
  mark: string;
  palette: string[];
  text: string;
};

export type ChatChartSize = {
  height: number;
  width: number;
};

type ChartRow = {
  label: string;
  value: number;
};

type LineRow = ChartRow & {
  series: string;
};

const MIN_WIDTH = 240;
const DEFAULT_WIDTH = 320;
const CHART_HEIGHT = 200;
const BAR_CORNER_RADIUS = 6;
const DONUT_CORNER_RADIUS = 4;
const DONUT_PAD_ANGLE = 0.02;
const DONUT_MIN_SIZE = 200;
const DONUT_MAX_SIZE = 280;

type BridgeKind = 'start' | 'increase' | 'decrease' | 'total';

export type BridgeRow = {
  connectorY?: number;
  kind: BridgeKind;
  label: string;
  nextLabel?: string;
  value: number;
  y0: number;
  y1: number;
};

const VALUE_FORMAT = ',~g';
const LINE_POINT_SIZE = 40;
const LINE_POINT_HOVER_SIZE = 90;

function toRows(recipe: ChatChartRecipe): ChartRow[] {
  return recipe.labels.map((label, index) => ({
    label,
    value: recipe.values[index] ?? 0,
  }));
}

export function toBridgeRows(recipe: ChatChartRecipe): BridgeRow[] {
  const count = recipe.labels.length;
  const rows: BridgeRow[] = [];
  let running = 0;

  for (let index = 0; index < count; index += 1) {
    const label = recipe.labels[index] ?? '';
    const value = recipe.values[index] ?? 0;
    const isFirst = index === 0;
    const isLast = index === count - 1;
    const y0 = isFirst || isLast ? 0 : running;
    const y1 = isFirst || isLast ? value : running + value;
    const kind: BridgeKind = isFirst
      ? 'start'
      : isLast
        ? 'total'
        : value < 0
          ? 'decrease'
          : 'increase';

    rows.push({
      kind,
      label,
      value,
      y0,
      y1,
    });
    running = isLast ? running : y1;
  }

  for (let index = 0; index < rows.length - 1; index += 1) {
    const row = rows[index];
    const next = rows[index + 1];
    if (row == null || next == null) {
      continue;
    }
    row.connectorY = row.y1;
    row.nextLabel = next.label;
  }

  return rows;
}

function chartWidth(width?: number): number {
  if (width == null || !Number.isFinite(width) || width <= 0) {
    return DEFAULT_WIDTH;
  }

  return Math.max(MIN_WIDTH, Math.floor(width));
}

function paletteRange(colors: ChatChartColors): string[] {
  return colors.palette.length > 0 ? colors.palette : [colors.mark];
}

function colorScale(colors: ChatChartColors, field = 'label') {
  return {
    name: 'color',
    type: 'ordinal' as const,
    domain: { data: 'table', field },
    range: paletteRange(colors),
  };
}

function chartConfig(colors: ChatChartColors) {
  return {
    axis: { labelFont: colors.font, titleFont: colors.font },
    font: colors.font,
    legend: {
      labelFont: colors.font,
      layout: {
        right: { anchor: 'middle' as const },
        top: { anchor: 'middle' as const },
      },
      titleFont: colors.font,
    },
    text: { font: colors.font },
  };
}

function roundedCap(positiveWhen: string) {
  return {
    cornerRadiusBottomLeft: {
      signal: `${positiveWhen} ? 0 : ${BAR_CORNER_RADIUS}`,
    },
    cornerRadiusBottomRight: {
      signal: `${positiveWhen} ? 0 : ${BAR_CORNER_RADIUS}`,
    },
    cornerRadiusTopLeft: {
      signal: `${positiveWhen} ? ${BAR_CORNER_RADIUS} : 0`,
    },
    cornerRadiusTopRight: {
      signal: `${positiveWhen} ? ${BAR_CORNER_RADIUS} : 0`,
    },
  };
}

function wrapsCategoryLabel(label: string): boolean {
  return label.length > 10 && label.includes(' ');
}

function categoryAxis(colors: ChatChartColors, rows: Array<{ label: string }>) {
  const wraps = rows.some((row) => wrapsCategoryLabel(row.label));
  return {
    domain: false,
    encode: {
      labels: {
        update: {
          text: {
            signal:
              "datum.value.length > 10 && indexof(datum.value, ' ') >= 0 ? split(datum.value, ' ') : datum.value",
          },
        },
      },
    },
    labelAlign: 'center' as const,
    labelAngle: 0,
    labelBaseline: 'top' as const,
    labelBound: false,
    labelColor: colors.text,
    labelFont: colors.font,
    labelFontSize: 11,
    labelLimit: wraps ? 96 : 80,
    labelOverlap: 'greedy' as const,
    labelPadding: 6,
    labelSeparation: 4,
    orient: 'bottom' as const,
    scale: 'x',
    ticks: false,
  };
}

function valueAxis(colors: ChatChartColors) {
  return {
    domain: false,
    grid: true,
    gridColor: colors.grid,
    labelColor: colors.text,
    labelFont: colors.font,
    labelFontSize: 11,
    orient: 'left' as const,
    scale: 'y',
    tickCount: 3,
    ticks: false,
  };
}

function categoryBottom(rows: Array<{ label: string }>): number {
  return rows.some((row) => wrapsCategoryLabel(row.label)) ? 36 : 18;
}

function formatValue(field = 'datum.value'): string {
  return `format(${field}, '${VALUE_FORMAT}')`;
}

function labelValueTooltip(valueField = 'datum.value'): string {
  return `datum.label + ': ' + ${formatValue(valueField)}`;
}

function seriesValueTooltip(): string {
  return `datum.series != '' ? datum.series + ' · ' + ${labelValueTooltip()} : ${labelValueTooltip()}`;
}

function donutValueTooltip(): string {
  return `${labelValueTooltip()} + ' (' + format((datum.endAngle - datum.startAngle) / (2 * PI), '.0%') + ')'`;
}

function hoverTooltip(signal: string) {
  return {
    cursor: { value: 'pointer' as const },
    tooltip: { signal },
  };
}

function barSpec(
  rows: ChartRow[],
  colors: ChatChartColors,
  width: number
): Spec {
  return {
    $schema: 'https://vega.github.io/schema/vega/v5.json',
    width,
    height: CHART_HEIGHT,
    autosize: { type: 'fit', contains: 'padding' },
    padding: { left: 8, right: 8, top: 8, bottom: categoryBottom(rows) },
    data: [{ name: 'table', values: rows }],
    scales: [
      {
        name: 'x',
        type: 'band',
        domain: { data: 'table', field: 'label' },
        range: 'width',
        padding: 0.2,
        round: true,
      },
      {
        name: 'y',
        type: 'linear',
        domain: { data: 'table', field: 'value' },
        nice: true,
        range: 'height',
      },
      colorScale(colors),
    ],
    axes: [categoryAxis(colors, rows), valueAxis(colors)],
    marks: [
      {
        type: 'rect',
        from: { data: 'table' },
        encode: {
          enter: {
            x: { scale: 'x', field: 'label' },
            width: { scale: 'x', band: 1 },
            y: { scale: 'y', field: 'value' },
            y2: { scale: 'y', value: 0 },
            fill: { scale: 'color', field: 'label' },
            ...roundedCap('datum.value >= 0'),
          },
          update: {
            fillOpacity: { value: 1 },
            ...hoverTooltip(labelValueTooltip()),
          },
          hover: {
            fillOpacity: { value: 0.72 },
          },
        },
      },
    ],
  };
}

function toLineRows(recipe: ChatChartRecipe): LineRow[] {
  const series =
    recipe.series != null && recipe.series.length > 1
      ? recipe.series
      : [{ name: '', values: recipe.values }];

  return series.flatMap((item) =>
    recipe.labels.map((label, index) => ({
      label,
      series: item.name,
      value: item.values[index] ?? 0,
    }))
  );
}

function linePointMarks(
  data: string,
  stroke: { field: string } | { value: string }
) {
  const color =
    'field' in stroke
      ? { scale: 'color', field: stroke.field }
      : { value: stroke.value };
  const hovered =
    'hoverPoint && hoverPoint.label === datum.label && hoverPoint.series === datum.series';

  return [
    {
      type: 'line' as const,
      from: { data },
      encode: {
        enter: {
          interpolate: { value: 'monotone' as const },
          x: { scale: 'x', field: 'label' },
          y: { scale: 'y', field: 'value' },
          stroke: color,
          strokeWidth: { value: 2 },
        },
      },
    },
    {
      type: 'symbol' as const,
      from: { data },
      encode: {
        enter: {
          x: { scale: 'x', field: 'label' },
          y: { scale: 'y', field: 'value' },
          fill: color,
        },
        update: {
          size: {
            signal: `${hovered} ? ${LINE_POINT_HOVER_SIZE} : ${LINE_POINT_SIZE}`,
          },
          ...hoverTooltip(seriesValueTooltip()),
        },
      },
    },
  ];
}

function lineHoverData() {
  return {
    name: 'hover',
    source: 'table',
    transform: [
      { type: 'formula' as const, expr: 'scale("x", datum.label)', as: 'hx' },
      { type: 'formula' as const, expr: 'scale("y", datum.value)', as: 'hy' },
      {
        type: 'voronoi' as const,
        x: 'hx',
        y: 'hy',
        as: 'path',
        size: [
          { signal: 'width' },
          { signal: 'height' },
        ] as [{ signal: string }, { signal: string }],
      },
    ],
  };
}

function lineHoverCell() {
  return {
    type: 'path' as const,
    name: 'hovercell',
    from: { data: 'hover' },
    encode: {
      update: {
        fill: { value: 'transparent' },
        path: { field: 'path' },
        ...hoverTooltip(seriesValueTooltip()),
      },
    },
  };
}

function lineHoverSignal() {
  return {
    name: 'hoverPoint',
    value: null,
    on: [
      { events: '@hovercell:mouseover', update: 'datum' },
      { events: '@hovercell:mouseout', update: 'null' },
    ],
  };
}

function lineSpec(
  recipe: ChatChartRecipe,
  colors: ChatChartColors,
  width: number
): Spec {
  const rows = toLineRows(recipe);
  const multiSeries = recipe.series != null && recipe.series.length > 1;
  const stroke = paletteRange(colors)[0] ?? colors.mark;
  return {
    $schema: 'https://vega.github.io/schema/vega/v5.json',
    width,
    height: CHART_HEIGHT,
    autosize: { type: 'fit', contains: 'padding' },
    padding: {
      left: 8,
      right: 8,
      top: multiSeries ? 12 : 8,
      bottom: categoryBottom(rows),
    },
    data: [{ name: 'table', values: rows }, lineHoverData()],
    signals: [lineHoverSignal()],
    scales: [
      {
        name: 'x',
        type: 'point',
        domain: { data: 'table', field: 'label' },
        range: 'width',
        padding: 0.5,
      },
      {
        name: 'y',
        type: 'linear',
        domain: { data: 'table', field: 'value' },
        nice: true,
        zero: false,
        range: 'height',
      },
      ...(multiSeries ? [colorScale(colors, 'series')] : []),
    ],
    axes: [categoryAxis(colors, rows), valueAxis(colors)],
    ...(multiSeries
      ? {
          legends: [
            {
              fill: 'color',
              direction: 'horizontal' as const,
              labelColor: colors.text,
              labelFont: colors.font,
              labelFontSize: 11,
              offset: 4,
              orient: 'top' as const,
              symbolType: 'circle',
            },
          ],
        }
      : {}),
    marks: [
      ...(multiSeries
        ? [
            {
              type: 'group' as const,
              from: {
                facet: {
                  data: 'table',
                  groupby: 'series',
                  name: 'split',
                },
              },
              marks: linePointMarks('split', { field: 'series' }),
            },
          ]
        : linePointMarks('table', { value: stroke })),
      lineHoverCell(),
    ],
  };
}

function donutSize(width: number): number {
  return Math.min(
    Math.max(Math.round(width * 0.42), DONUT_MIN_SIZE),
    DONUT_MAX_SIZE
  );
}

function donutSpec(
  rows: ChartRow[],
  colors: ChatChartColors,
  size: number
): Spec {
  return {
    $schema: 'https://vega.github.io/schema/vega/v5.json',
    width: size,
    height: size,
    autosize: { type: 'pad', contains: 'padding' },
    padding: 8,
    data: [
      {
        name: 'table',
        values: rows,
        transform: [{ type: 'pie', field: 'value' }],
      },
    ],
    scales: [colorScale(colors)],
    legends: [
      {
        fill: 'color',
        labelColor: colors.text,
        labelFont: colors.font,
        labelFontSize: 11,
        // A signal keeps the limit responsive without rebuilding the spec.
        labelLimit: { signal: 'max(120, width * 0.85)' },
        offset: 16,
        orient: 'right',
        symbolType: 'circle',
      },
    ],
    marks: [
      {
        type: 'arc',
        from: { data: 'table' },
        encode: {
          enter: {
            fill: { scale: 'color', field: 'label' },
          },
          // The center rides `update` alongside the radii; Vega only replays
          // `enter` for new items, so a live resize would otherwise grow the
          // ring around the old center.
          update: {
            startAngle: { field: 'startAngle' },
            endAngle: { field: 'endAngle' },
            padAngle: { value: DONUT_PAD_ANGLE },
            innerRadius: { signal: 'width * 0.26' },
            outerRadius: { signal: 'width * 0.42' },
            cornerRadius: { value: DONUT_CORNER_RADIUS },
            x: { signal: 'width / 2' },
            y: { signal: 'height / 2' },
            ...hoverTooltip(donutValueTooltip()),
          },
          hover: {
            outerRadius: { signal: 'width * 0.46' },
          },
        },
      },
    ],
  };
}

function bridgeSpec(
  rows: BridgeRow[],
  colors: ChatChartColors,
  width: number
): Spec {
  return {
    $schema: 'https://vega.github.io/schema/vega/v5.json',
    width,
    height: CHART_HEIGHT,
    autosize: { type: 'fit', contains: 'padding' },
    padding: { left: 8, right: 8, top: 8, bottom: categoryBottom(rows) },
    data: [
      { name: 'table', values: rows },
      {
        name: 'connectors',
        source: 'table',
        transform: [{ type: 'filter', expr: 'isValid(datum.nextLabel)' }],
      },
    ],
    scales: [
      {
        name: 'x',
        type: 'band',
        domain: { data: 'table', field: 'label' },
        range: 'width',
        padding: 0.2,
        round: true,
      },
      {
        name: 'y',
        type: 'linear',
        domain: { data: 'table', fields: ['y0', 'y1'] },
        nice: true,
        zero: true,
        range: 'height',
      },
      colorScale(colors),
    ],
    axes: [categoryAxis(colors, rows), valueAxis(colors)],
    marks: [
      {
        type: 'rect',
        from: { data: 'table' },
        encode: {
          enter: {
            x: { scale: 'x', field: 'label' },
            width: { scale: 'x', band: 1 },
            y: { scale: 'y', field: 'y1' },
            y2: { scale: 'y', field: 'y0' },
            fill: { scale: 'color', field: 'label' },
            ...roundedCap('datum.y1 >= datum.y0'),
          },
          update: {
            fillOpacity: { value: 1 },
            ...hoverTooltip(labelValueTooltip()),
          },
          hover: {
            fillOpacity: { value: 0.72 },
          },
        },
      },
      {
        type: 'rule',
        from: { data: 'connectors' },
        encode: {
          enter: {
            x: { scale: 'x', field: 'label', band: 1 },
            x2: { scale: 'x', field: 'nextLabel' },
            y: { scale: 'y', field: 'connectorY' },
            y2: { scale: 'y', field: 'connectorY' },
            stroke: { value: colors.text },
            strokeWidth: { value: 1 },
            opacity: { value: 0.35 },
          },
        },
      },
    ],
  };
}

/**
 * The size the chart should occupy for a measured container width.
 *
 * Kept out of {@link createChatChartSpec} so a resize can ride react-vega's
 * `width`/`height` props, which resize the live Vega view instead of parsing a
 * new spec and rebuilding the SVG on every step of a panel drag.
 */
export function chatChartSize(
  recipe: ChatChartRecipe,
  width?: number
): ChatChartSize {
  const resolvedWidth = chartWidth(width);
  if (recipe.type === 'donut') {
    const size = donutSize(resolvedWidth);
    return { height: size, width: size };
  }

  return { height: CHART_HEIGHT, width: resolvedWidth };
}

export function createChatChartSpec(
  recipe: ChatChartRecipe,
  colors: ChatChartColors
): Spec {
  const size = chatChartSize(recipe);
  let spec: Spec;
  if (recipe.type === 'line') {
    spec = lineSpec(recipe, colors, size.width);
  } else if (recipe.type === 'donut') {
    spec = donutSpec(toRows(recipe), colors, size.width);
  } else if (recipe.type === 'bridge') {
    spec = bridgeSpec(toBridgeRows(recipe), colors, size.width);
  } else {
    spec = barSpec(toRows(recipe), colors, size.width);
  }

  return { ...spec, config: chartConfig(colors) };
}
